import React from 'react';
import { useLingui } from '@lingui/react/macro';
import { z } from 'zod';

import { Box, Heading } from '@rover/kibble/core';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet';
import Modal from '@rover/kibble/official-patterns/Modal';
import type { HomeAddressUpdate } from '@rover/rsdk/src/apiClient/latest/model/homeAddressUpdate';
import type { UserInformationAddress } from '@rover/rsdk/src/apiClient/latest/model/userInformationAddress';
import { isMobileBrowser } from '@rover/rsdk/src/modules/Network/userAgent';
import { KibbleAddressForm } from '@rover/shared/js/components/KibbleAddressForm';
import emitMetric from '@rover/utilities/emitMetric';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

import useHomeAddress from '../../hooks/useHomeAddress';
import useTemporaryAddress from '../../hooks/useTemporaryAddress';
import { AddressType } from '../../LocationInput.constants';
import { areAddressesDifferent } from '../../utils';

import FormFields from './FormFields';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onEdit: (address: UserInformationAddress) => void;
  isEditMode?: boolean;
  addressType: AddressType;
  address?: Partial<UserInformationAddress>;
};

export default function AddressModal({
  isOpen,
  onClose,
  onEdit,
  addressType,
  address,
  isEditMode = false,
}: Props): JSX.Element {
  const { t } = useLingui();
  const isMobile = isMobileBrowser();
  const {
    saveAddress: saveHomeAddress,
    isLoading: isSavingHomeAddress,
    address: homeAddress,
    submissionErrors,
  } = useHomeAddress(true);
  const { address: temporaryAddress, setTemporaryAddress } = useTemporaryAddress();

  const handleFormSubmit = (data: HomeAddressUpdate & { isHomeAddress?: string[] }): void => {
    const { isHomeAddress, ...addressData } = data;
    const isHomeAddressChecked = isHomeAddress?.includes('home');

    if (isHomeAddressChecked) {
      saveHomeAddress({
        data: addressData,
        onSuccess: (newAddressData) => {
          // Clear temporary address if it matches the new home address
          if (newAddressData && temporaryAddress) {
            const addressesMatch = !areAddressesDifferent(newAddressData, temporaryAddress);
            if (addressesMatch) {
              setTemporaryAddress(null);
            }
          }

          emitMetric('contact_sitter_form_new_home_address_alert_displayed');
          fireDataLayerEvent({
            event: 'contact_sitter_form_new_home_address_alert_displayed',
          });

          if (newAddressData) {
            onEdit(newAddressData);
          } else {
            onEdit(addressData as unknown as UserInformationAddress);
          }
          onClose();
        },
      });
    } else {
      setTemporaryAddress(addressData as unknown as UserInformationAddress);
      onEdit(addressData as unknown as UserInformationAddress);
      onClose();
    }
  };

  const headerText = isEditMode ? t`Edit Address` : t`Add Address`;

  const extraSchema = z.object({
    isHomeAddress: z.array(z.string()).optional(),
  });

  const display = isMobile
    ? {
        Component: BottomSheet,
        props: {
          hideCloseIcon: true,
          overflowY: 'auto' as const,
          closeOnDragDown: true,
        },
      }
    : {
        Component: Modal,
        props: {
          showCloseIcon: true,
          headerText,
          contentProps: {
            width: 'min(600px, calc(100vw - 48px))', // second value is for small screens
            maxHeight: '90vh',
            overflowY: 'auto' as const,
          },
        },
      };

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <display.Component isOpen={isOpen} onRequestClose={onClose} {...display.props}>
        <Box>
          {isMobile && (
            <Heading size="300" mb="2x">
              {headerText}
            </Heading>
          )}
          <KibbleAddressForm
            onSubmit={handleFormSubmit}
            defaultValues={{
              isHomeAddress: addressType === AddressType.Home ? ['home'] : [],
              ...address,
            }}
            extraSchema={extraSchema}
            fieldsHeight={isMobile ? undefined : '450px'}
            isLoading={isSavingHomeAddress}
            homeCountryCode={homeAddress?.countryCode}
            submissionErrors={submissionErrors}
            suppressInlineAlerts
          >
            {({ configureField, getValues, watch, setValue }) => (
              <FormFields
                configureField={configureField}
                getValues={getValues}
                watch={watch}
                setValue={setValue}
                addressType={addressType}
              />
            )}
          </KibbleAddressForm>
        </Box>
      </display.Component>
    </Box>
  );
}
